import { useEffect } from "react";
import { Bell, X } from "lucide-react";
import { notify } from "@/lib/notify";
import { cn } from "@/lib/utils";
import { router } from "@/router";
import { sound } from "@/lib/sound";
import { Logger } from "@/lib/logger";
import { listenForegroundMessages } from "@/lib/push/messaging";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { claimNotificationToast } from "@/lib/notification-dedupe";

const log = new Logger("Push");

function ForegroundPushToast({
  title,
  body,
  toastId,
  onNavigate,
}: {
  title: string;
  body: string;
  toastId: string | number;
  onNavigate: () => void;
}) {
  return (
    <div
      onClick={() => {
        onNavigate();
        notify.dismiss(toastId);
      }}
      style={{ background: "#1e293b" }}
      className="flex items-start gap-3 px-4 py-3.5 rounded-2xl shadow-2xl w-[340px] border border-white/10 cursor-pointer hover:brightness-110 transition-all"
    >
      <div
        style={{ color: "#818cf8", background: "#818cf820" }}
        className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
      >
        <Bell size={20} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white font-black text-sm leading-tight">{title}</p>
        <p className="text-white/60 text-xs font-medium mt-0.5 leading-snug">{body}</p>
        <p className="text-white/40 text-[10px] font-black uppercase tracking-widest mt-1.5">
          Cliquer pour voir →
        </p>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          notify.dismiss(toastId);
        }}
        className={cn(
          "shrink-0 mt-0.5 p-1 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-colors",
        )}
        aria-label="Fermer"
      >
        <X size={14} />
      </button>
    </div>
  );
}

/**
 * Monté sous `EchoProvider` (donc uniquement session authentifiée) : pilote la synchronisation
 * silencieuse du jeton push web (`usePushNotifications`, cf. `docs/PWA_PUSH_NOTIFICATIONS.md` §3.2
 * points 5-6) et affiche un toast pour les push reçus **au premier plan** — le service worker
 * (`onBackgroundMessage`) ne se déclenche jamais pour ceux-là, FCM choisit l'un ou l'autre selon
 * l'état de l'onglet, jamais les deux.
 *
 * Ce canal push reste toutefois actif en parallèle d'Echo/Pusher (`useNotificationChannel`), qui
 * annonce la même notification en temps réel dès qu'un onglet est ouvert — le backend envoie le
 * push à tous les tokens actifs sans attendre que l'app soit en arrière-plan. `claimNotificationToast`
 * (cf. `notification-dedupe.ts`) tranche : premier des deux canaux arrivé, affiché ; l'autre est tu.
 * On garde volontairement les deux canaux vivants plutôt que de désactiver l'un des deux : si Echo
 * est déconnecté au moment de l'envoi, le push reste le seul filet.
 */
export function PushSubscriber() {
  // Déclenche les effets de synchronisation silencieuse du hook (support → permission déjà
  // accordée → jeton → enregistrement / re-enregistrement). Rien d'autre n'est utilisé ici : le
  // reste de l'état sert au toggle des réglages (`NotificationsTab`).
  usePushNotifications();

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    let cancelled = false;

    listenForegroundMessages(({ title, body, data }) => {
      const notificationId = data?.notification_id;

      // Le même événement backend arrive aussi par Echo (`useNotificationChannel`), quasi
      // simultanément quand l'onglet est au premier plan — cf. `notification-dedupe.ts`. Sans ce
      // garde-fou, l'utilisateur voit deux toasts et entend deux sons pour une seule notification.
      if (claimNotificationToast(notificationId)) {
        log.debug("Push premier plan ignoré (déjà annoncé par Echo)", { notificationId });
        return;
      }

      sound.notification();
      const target = notificationId
        ? `/n/${encodeURIComponent(notificationId)}${
            data?.type ? `?type=${encodeURIComponent(data.type)}` : ""
          }`
        : null;

      notify.custom(
        (t) => (
          <ForegroundPushToast
            title={title ?? "Success School"}
            body={body ?? ""}
            toastId={t}
            onNavigate={() => {
              if (target) router.navigate(target);
            }}
          />
        ),
        { duration: 6000 },
      );
    })
      .then((unsub) => {
        if (cancelled) {
          unsub?.();
          return;
        }
        unsubscribe = unsub;
      })
      .catch((err) => {
        log.warn("Échec d'abonnement aux push premier plan", err);
      });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  return null;
}
